export default function AgentPagerPrivacy() {
  return (
    <main className="min-h-screen bg-[#FBFAF6] text-[#26313B]">
      <section className="mx-auto max-w-3xl px-6 py-12">
        <header className="mb-8 text-center">
          <h1 className="text-4xl font-extrabold tracking-tight text-[#A8720E]">
            Privacy Policy — AgentPager
          </h1>
          <p className="mt-2 text-sm text-[#5B6B7C]">
            Effective date: <time dateTime="2026-09-23">September 23, 2026</time>
          </p>
          <p className="mt-1 text-xs text-[#8A93A2]">
            Published by <span className="font-semibold">Cosmovex</span>
          </p>
        </header>

        <div className="space-y-8 leading-relaxed text-[#3D4B59]">
          <p>
            <strong>Cosmovex</strong> built <strong>AgentPager</strong> (formerly
            MDPilot), an Android app that lets you follow and control AI coding
            agents — such as Claude Code, Codex, Gemini CLI and OpenCode —
            running on your own computer, and that includes a markdown toolkit.
            This Privacy Policy explains what information AgentPager processes,
            how it is used, and the choices you have.
          </p>
          <p>
            AgentPager is <strong>account-free</strong>. There is no sign-up and
            no login. Your documents are stored on your device, and your coding
            sessions stay on your computer.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Information We Process
          </h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              <strong>Agent remote (end-to-end encrypted):</strong> when you
              pair a computer, your phone and that computer exchange encryption
              keys through the QR code. Everything they send each other —
              session titles, messages, approval requests, replies — is
              encrypted on one device and only decrypted on the other. It
              travels through Google Firebase Realtime Database, which stores
              only encrypted data that neither Google nor we can read, and each
              message is deleted once it is delivered. Firebase necessarily
              sees technical metadata such as a random channel identifier,
              timestamps, message sizes and IP addresses. Your keys are kept in
              Android&apos;s secure storage on your phone and in your user
              folder on your computer.
            </li>
            <li>
              <strong>Project folders:</strong> so you can start a session from
              your phone, your computer sends it a list of <em>folder names</em>
              — never file contents — encrypted in the same way. Your computer
              only offers folders inside your home folder, the folder you
              started AgentPager in, or beside a project you already have a
              session in, and never hidden folders. We never see any of it.
            </li>
            <li>
              <strong>Recent activity, kept on your phone:</strong> so the app can still show what
              happened when your computer is off, it keeps the last few sessions and their recent
              lines in its own private storage on the phone. It is never uploaded, it is not
              readable by other apps, pending approvals are never kept, and removing a computer (or
              uninstalling) deletes it.
            </li>
            <li>
              <strong>Notifications:</strong> if you allow notifications, your
              phone&apos;s Firebase Cloud Messaging token is sent (encrypted) to
              your own paired computer. When an agent needs you or finishes,
              your computer sends an encrypted notification through our small
              relay service (hosted on Vercel) to Google Firebase Cloud
              Messaging. The relay passes the token and the encrypted payload
              on and does not store them; your phone decrypts the text.
            </li>
            <li>
              <strong>Camera:</strong> used only to scan the pairing QR code.
              The camera image is processed on your device and is not stored or
              sent anywhere.
            </li>
            <li>
              <strong>Microphone and voice:</strong> used only when you tap the
              microphone or use hands-free mode. Speech is turned into text by
              your phone&apos;s speech recognition service (provided by Google
              or your device maker), which may process the audio on its own
              servers under its own privacy policy. We never receive your
              audio. Spoken replies use your phone&apos;s text-to-speech engine.
            </li>
            <li>
              <strong>Your documents:</strong> the markdown you write is stored
              on your device. If you choose a folder, files are saved there via
              Android&apos;s document picker. Document contents are never
              uploaded automatically.
            </li>
            <li>
              <strong>AI features (optional):</strong> when you use an AI action
              (for example, generating or linting an agent-config file), the
              text you submit for that action is sent to the AI provider
              (Google&apos;s Gemini API) to produce a response. If you connect
              your own API key, the request goes directly using your key. AI is
              only invoked when you tap an AI action.
            </li>
            <li>
              <strong>LAN Vault Server (Pro):</strong> when you start it, your
              documents are served only to devices on your own Wi-Fi network.
              Nothing is sent to us or to the internet.
            </li>
            <li>
              <strong>Usage analytics:</strong> we use Google Firebase Analytics
              to understand which features are used and where people get stuck
              (for example: paired a computer, opened a session, approved a
              request, which agent type). We do not collect the content of your
              documents, messages, code, commands or file paths.
            </li>
            <li>
              <strong>Crash reports:</strong> Google Firebase Crashlytics
              collects diagnostic data if the app crashes, to help us fix bugs.
            </li>
            <li>
              <strong>Advertising:</strong> the free version shows ads via Google
              AdMob, which may use an advertising identifier. AgentPager Pro removes
              ads.
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Information We Do Not Collect
          </h2>
          <p>
            We do not require an account. We do not collect your name, email, or
            contacts. We cannot read your coding sessions or messages, and we do
            not upload your documents to our servers.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Third-Party Services
          </h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              Google Firebase (Analytics, Crashlytics, Remote Config, Realtime
              Database as an encrypted relay, Cloud Messaging for notifications)
            </li>
            <li>Vercel (hosts the notification relay; stores nothing)</li>
            <li>Your device&apos;s speech recognition and text-to-speech services</li>
            <li>Google AdMob (advertising)</li>
            <li>Google Gemini API (only when you use AI features)</li>
            <li>
              Google Play Billing (for AgentPager Pro subscriptions and one-time
              purchases — we never receive your payment details)
            </li>
          </ul>
          <p>
            These services have their own privacy policies governing the data
            they process.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Children</h2>
          <p>
            AgentPager is intended for users aged 13 and older and is not
            directed at children.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Your Choices</h2>
          <p>
            You can use the toolkit fully offline. You can unpair a computer at
            any time in the app (and on the computer with{" "}
            <code>agentpager unpair</code>), which deletes its keys. You can turn
            notifications, the camera and the microphone off in Android
            settings. You can cancel a Pro subscription in Google Play. You can
            reset your advertising identifier in Android settings. Uninstalling
            the app deletes everything it stored on your phone.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Contact</h2>
          <p>
            Questions about this policy? Email{" "}
            <a
              className="text-[#0F6BBF] underline"
              href="mailto:trionapps@gmail.com"
            >
              trionapps@gmail.com
            </a>
            .
          </p>
        </div>
      </section>
    </main>
  );
}
